import { t } from 'i18next';
import { Controller, useWatch } from 'react-hook-form';
import dayjs from 'dayjs';
import {
  Autocomplete,
  Box,
  FormControl,
  InputAdornment,
  TextField,
} from '@mui/material';
import { Search } from '@mui/icons-material';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import {
  RadioButtonFormField,
  SelectFormField,
  TextFormField,
  DatepickerFormField,
} from '@components';
import { useOtherInsuranceEntry } from './hooks/useOtherInsuranceEntry';

const paymentTypeOptions = [
  { value: 'none', label: 'None' },
  { value: 'insurance', label: 'Insurance' },
  { value: 'cash', label: 'Cash' },
  { value: 'cash_community', label: 'Cash community' },
  { value: 'cash_poa', label: 'Cash POA' },
  { value: 'cash_self', label: 'Cash self' },
];

const relationshipOptions = [
  { value: 'child', label: 'Child' },
  { value: 'spouse', label: 'Spouse' },
  { value: 'mother', label: 'Mother' },
  { value: 'father', label: 'Father' },
  { value: 'injured_plaintiff', label: 'Injured plaintiff' },
  { value: 'other', label: 'Other' },
];

interface IOtherInsuranceEntryProps {
  index: number;
  onRemove: (index: number) => void;
  insuranceIdLabel: string;
  overlapError?: string;
}

export const OtherInsuranceEntry = ({
  index,
  onRemove: _onRemove,
  insuranceIdLabel,
  overlapError,
}: IOtherInsuranceEntryProps) => {
  const {
    control,
    setValue,
    payers,
    payersQuery,
    setPayersQuery,
    isPatientFieldValue,
    isConsentGivenByRequired,
    isCashPayment,
  } = useOtherInsuranceEntry({ index });

  const startDate = useWatch({ control, name: `entries.${index}.start_date` });
  const minEndDate = startDate ? dayjs(startDate) : undefined;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <SelectFormField
        name={`entries.${index}.payment_type`}
        label='Payment type'
        options={paymentTypeOptions}
        optional={false}
      />

      {!isCashPayment && (
        <>
          <Controller
            name={`entries.${index}.insurance_company`}
            control={control}
            render={({ field: { value, onChange }, fieldState: { error } }) => (
              <Autocomplete
                disablePortal
                options={payers}
                noOptionsText={t(
                  `patient.detailsCard.editPrimaryInsurance.${payersQuery.length > 1 ? 'noOptions' : 'typeTwoLetters'}`,
                )}
                getOptionLabel={(option: { id: string; name: string }) =>
                  option.name
                }
                filterOptions={options => options}
                onChange={(_, newValue) => {
                  onChange(newValue?.id ?? '');
                  setPayersQuery(newValue?.name || '');
                  setValue(
                    `entries.${index}.insurance_company_name`,
                    newValue?.name ?? '',
                  );
                }}
                value={payers.find(p => p.id === value) ?? null}
                renderInput={params => (
                  <TextField
                    {...params}
                    required
                    value={payersQuery}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                      setPayersQuery(e.target.value)
                    }
                    label='Payer name'
                    error={!!error}
                    helperText={error?.message}
                    slotProps={{
                      ...params.slotProps,
                      input: {
                        ...params.slotProps.input,
                        startAdornment: (
                          <InputAdornment position='start'>
                            <Search fontSize='small' />
                          </InputAdornment>
                        ),
                      },
                    }}
                  />
                )}
              />
            )}
          />

          <TextFormField
            name={`entries.${index}.insurance_id`}
            label={insuranceIdLabel}
            optional={false}
          />

          <TextFormField
            name={`entries.${index}.group_number`}
            label='Group (optional)'
            optional={true}
          />

          <TextFormField
            name={`entries.${index}.mbi`}
            label='Medicare Beneficiary Identifier (MBI) (optional)'
            optional={true}
          />

          <TextFormField
            name={`entries.${index}.plan_name`}
            label='Plan name (optional)'
            optional={true}
          />
        </>
      )}

      <Controller
        name={`entries.${index}.start_date`}
        control={control}
        render={({ field, fieldState: { error } }) => {
          const displayError = overlapError || error?.message;
          return (
            <FormControl fullWidth>
              <LocalizationProvider
                dateAdapter={AdapterDayjs}
                adapterLocale='en'
              >
                <DatePicker
                  label='Start date *'
                  value={field.value ? dayjs(field.value) : null}
                  onChange={val => {
                    field.onChange(
                      val && dayjs(val).isValid()
                        ? dayjs(val).format('YYYY-MM-DD')
                        : '',
                    );
                    setValue(`entries.${index}.end_date`, '');
                  }}
                  slotProps={{
                    field: { clearable: true },
                    textField: {
                      error: !!displayError,
                      helperText: displayError,
                    },
                  }}
                />
              </LocalizationProvider>
            </FormControl>
          );
        }}
      />

      <DatepickerFormField
        name={`entries.${index}.end_date`}
        label='End date (optional)'
        optional={true}
        minDate={minEndDate}
      />

      {!isCashPayment && (
        <>
          <RadioButtonFormField
            name={`entries.${index}.subscriber.is_patient`}
            label='Subscriber'
            optional={false}
            options={[
              { value: 'true', label: 'The patient is the subscriber' },
              { value: 'false', label: 'Someone else is the subscriber' },
            ]}
          />

          {isPatientFieldValue === 'false' && (
            <>
              <SelectFormField
                name={`entries.${index}.subscriber.relationship`}
                label='Patient relationship to subscriber'
                options={relationshipOptions}
                optional={false}
              />
              <TextFormField
                name={`entries.${index}.subscriber.subscriber_id`}
                label='Subscriber ID (optional)'
                optional={true}
              />
            </>
          )}
        </>
      )}

      <DatepickerFormField
        name={`entries.${index}.consent.verbal_consent_date`}
        label='Verbal consent date (optional)'
        optional={true}
        disableFuture
      />

      <TextFormField
        name={`entries.${index}.consent.given_by`}
        label={`Consent given by ${isConsentGivenByRequired ? '' : '(optional)'}`}
        optional={!isConsentGivenByRequired}
      />

      <TextFormField
        name={`entries.${index}.consent.witness`}
        label='Witness (optional)'
        optional={true}
      />

      <TextFormField
        name={`entries.${index}.comments`}
        label='Comments (optional)'
        optional={true}
      />
    </Box>
  );
};
